///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ValidationAlertModel, ValidationAlertType } from './validation-alert.model';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-validation-alert',
    template: ` <div
        class="alert"
        [ngClass]="messageType"
        role="alert"
        *ngIf="showAlert"
        [translate]="messageText"
        [translateParams]="messageParams"
    ></div>`,
    standalone: false
})
export class GtthValidationAlertComponent implements OnInit, OnDestroy {
    @Input() showAlert: boolean;
    @Output() showAlertChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() messageObservable: Observable<string | ValidationAlertModel>;
    private message: string | ValidationAlertModel;

    private subscription: Subscription;
    private readonly SHOW_TIMEOUT = 5000;
    private timeout: ReturnType<typeof setTimeout>;

    constructor(private readonly ngZone: NgZone) {}

    get messageText(): string {
        if (typeof this.message === 'object' && isDefined(this.message.text)) {
            return this.message.text;
        } else {
            return this.message as string;
        }
    }

    get messageParams(): Record<string, string> {
        let params = null;
        if (typeof this.message === 'object' && isDefined(this.message.params) && Object.keys(this.message.params).length) {
            params = this.message.params;
        }
        return params;
    }

    get messageType(): string {
        if (typeof this.message === 'object' && isDefined(this.message.type)) {
            return this.message.type;
        } else {
            return ValidationAlertType.DANGER;
        }
    }

    ngOnInit(): void {
        this.subscription = this.messageObservable.subscribe((message: string | ValidationAlertModel) => {
            if (this.timeout) {
                clearTimeout(this.timeout);
            }
            this.setMessage(message);

            if (this.showAlert) {
                this.initClearMessageTimeout();
            }
        });
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
    }

    private initClearMessageTimeout(): void {
        this.ngZone.runOutsideAngular(() => {
            this.timeout = setTimeout(() => {
                this.ngZone.run(() => {
                    this.setMessage('');
                });
            }, this.SHOW_TIMEOUT);
        });
    }

    private setMessage(message: string | ValidationAlertModel): void {
        this.message = message;
        if (typeof message === 'object' && isDefined(message.text)) {
            this.showAlert = message.text !== '';
        } else {
            this.showAlert = message !== '';
        }
        this.showAlertChange.emit(this.showAlert);
    }
}
